import { ArrowUpRight } from 'lucide-react';
import { useProjects } from '../../lib/hooks';

const fallbackProject = {
  title: 'AgriViT',
  description: 'An on-device crop disease detection app that brings computer vision closer to the field.',
  technologies: ['Flutter', 'TFLite', 'ONNX', 'Edge AI'],
};

const HomePage = ({ onNavigate }) => {
  const { projects, loading } = useProjects();
  const project = projects[0] || fallbackProject;
  const technologies = project.technologies || fallbackProject.technologies;

  return (
    <section className="max-w-5xl">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#22C55E]">01 / HOME</p>

      <div className="mt-6 max-w-4xl">
        <h1 className="text-4xl font-mono font-bold leading-tight text-[#EDEDEF] sm:text-6xl">
          Software Engineer building practical mobile, AI/ML, and IoT products.
        </h1>
        <p className="mt-6 max-w-3xl text-lg leading-8 text-[#8B8B93]">
          I ship production-ready applications that connect Flutter, applied machine learning, backend systems, and embedded hardware around real user and operational needs.
        </p>
      </div>

      <div className="mt-10 grid gap-px overflow-hidden rounded-xl border border-[#27272A] bg-[#27272A] sm:grid-cols-3">
        {[
          ['05', 'shipped products'],
          ['98%', 'AgriViT model accuracy'],
          ['2024', 'freelance since'],
        ].map(([value, label]) => (
          <div key={label} className="bg-[#141416] px-5 py-5">
            <p className="font-mono text-2xl font-bold text-[#4ADE80]">{value}</p>
            <p className="mt-2 text-xs uppercase tracking-wider text-[#8B8B93]">{label}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 border-t border-[#27272A] pt-8">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#8B8B93]">Featured project</p>
            <h2 className="mt-3 text-2xl font-mono font-bold text-[#EDEDEF]">{project.title}</h2>
          </div>
          <button
            type="button"
            onClick={() => onNavigate('projects')}
            className="inline-flex shrink-0 items-center gap-1.5 border-b border-[#22C55E]/40 pb-0.5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#22C55E] transition-all duration-200 hover:border-[#4ADE80] hover:text-[#4ADE80] hover:translate-x-0.5"
          >
            View all projects <ArrowUpRight size={15} />
          </button>
        </div>

        <article className="mt-5 rounded-xl border border-[#27272A] bg-[#141416] p-6 transition-colors hover:border-[#22C55E]/60">
          <div className="flex flex-wrap gap-2">
            {technologies.slice(0, 4).map((technology) => (
              <span key={technology} className="rounded-full border border-[#27272A] px-3 py-1 font-mono text-[11px] text-[#8B8B93]">
                {technology}
              </span>
            ))}
          </div>
          <div className="mt-5 flex items-baseline gap-3">
            <span className="font-mono text-2xl font-bold text-[#4ADE80]">98%</span>
            <span className="text-xs uppercase tracking-wider text-[#8B8B93]">AgriViT model accuracy</span>
          </div>
          <p className="mt-5 max-w-2xl leading-7 text-[#8B8B93]">
            {loading ? 'Loading project details...' : project.description || fallbackProject.description}
          </p>
        </article>
      </div>

      <p className="mt-12 max-w-3xl text-base leading-8 text-[#8B8B93]">
        5+ production apps, up to 99% model accuracy, and hands-on experience across mobile development, applied ML, IoT, teaching, and technical mentoring.
      </p>
    </section>
  );
};

export default HomePage;
